/**
 * Missing-generated-copy detection for the campaign detail poll (#5066, #6285).
 *
 * The campaign query keeps refetching while Celery-generated copy is still
 * landing. Pure module so the conditions are unit-testable away from the
 * query-options graph.
 */
import type { CampaignData } from './types';

export const MISSING_COPY_POLL_INTERVAL_MS = 3000;
// ≈2 minutes at 3s per poll. Description generation has been observed landing
// ~80s after campaign creation — well past both the creation flow's delayed
// refetches (3/6/12s) and the old ≈30s headline-only cap (#6285).
export const MISSING_COPY_MAX_POLLS = 40;
// Only FRESH campaigns poll for a missing description: older campaigns may
// legitimately have none, and would otherwise poll on every visit.
export const FRESH_CAMPAIGN_MAX_AGE_MS = 60 * 60 * 1000;

// Placeholder names are stubs awaiting async AI-generated names from Celery.
// Full names have a prefix like "Glitch | Google | Campaign bj24948S1o",
// so we check if the placeholder pattern appears anywhere in the name.
// Two stub formats exist:
//  1. Legacy ``Campaign <10-char-slug>`` (standard / brand / competitor)
//  2. Optimisation router stub (router.py:982): ``Generating <Type> Campaign…``
//     used by HiPo / ICP / Location / Intent. The trailing character is the
//     unicode ellipsis (U+2026); the legacy regex misses it, which left
//     ``refetchInterval`` permanently off for those campaigns (#4317).
export const isPlaceholderName = (name?: string) =>
  !!name && (
    /Campaign [a-zA-Z0-9]{10}$/.test(name) ||
    /^Generating .+ Campaign…$/.test(name)
  );

/**
 * True while any Celery-generated copy is still missing: an ad without
 * headlines, or — on a fresh campaign — an empty campaign description
 * (`business_desc`, the Overview's "Campaign Description" field, #6285).
 */
export function isMissingGeneratedCopy(
  data: Pick<CampaignData, 'ads' | 'business_desc' | 'created_at'>,
  now: number = Date.now(),
): boolean {
  if (data.ads.some((ad) => !ad.headlines || ad.headlines.length === 0)) return true;
  if (!data.business_desc) {
    const created = new Date(data.created_at).getTime();
    if (Number.isFinite(created) && now - created < FRESH_CAMPAIGN_MAX_AGE_MS) return true;
  }
  return false;
}
